"use client";

import { useMessages } from "@/lib/i18n/useMessages";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { ReactNode } from "react";

interface SLDialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  content?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  dialogProps?: Omit<DialogProps, "onClose" | "open">;
}

export const SLDialog = ({
  open,
  onClose,
  title,
  subtitle,
  content,
  actions,
  children,
  dialogProps,
}: SLDialogProps) => {
  const d = useMessages().dialogs;

  // If children are provided, use the old composition pattern
  if (children) {
    return (
      <Dialog
        onClose={onClose}
        open={open}
        aria-labelledby={title ? "dialog-title" : undefined}
        aria-describedby="dialog-content"
        {...dialogProps}
      >
        <Box sx={{ position: "relative" }} id="dialog-content">
          {/* Close button for composition pattern */}
          <IconButton
            onClick={onClose}
            aria-label="Close dialog"
            sx={{
              position: "absolute",
              boxShadow: "none",
              top: 8,
              right: 8,
              zIndex: 1000,
              backgroundColor: "transparent",
              color: "success.main",

              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
            size="small"
          >
            <Close />
          </IconButton>
          {title ? (
            <DialogTitle id="dialog-title" sx={{ pr: 6 }}>
              {title}
            </DialogTitle>
          ) : null}
          {children}
        </Box>
        <DialogActions>
          {actions}
          <Button variant="text" color="success" onClick={onClose}>
            {d.common.close}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise, use the new structured pattern
  return (
    <Dialog
      onClose={onClose}
      open={open}
      aria-labelledby={title ? "dialog-title" : undefined}
      aria-describedby={subtitle ? "dialog-description" : undefined}
      {...dialogProps}
      sx={{
        ...dialogProps?.sx,
        p: 0,
      }}
    >
      {title && <DialogTitle id="dialog-title">{title}</DialogTitle>}
      <DialogContent sx={{ height: "100%" }}>
        {subtitle && (
          <DialogContentText id="dialog-description" sx={{ mb: 4 }}>
            {subtitle}
          </DialogContentText>
        )}
        {content}
      </DialogContent>
      <DialogActions>
        {actions}
        <Button variant="text" color="success" onClick={onClose}>
          {d.common.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
